import React, { Dispatch, useEffect, useRef, useState } from 'react';
import Image from 'next/legacy/image';
import {
  CurrentUser,
  JoinedFormFieldProps,
  ListResponseData,
  ModalProps,
  ProductComment,
  ProductFavorite,
  SimpleItem,
} from 'types';
import { getGearComments, getReportReasons, postGearData } from 'api';
import { getTimeInterval, getUserFirstAndLastName } from 'utils/services';
import { getReportFieldProps, USER_PHOTO_PLACEHOLDER_LINK } from 'consts';
import { Button, ButtonLottie } from 'components/Button';
import { SnackbarProps } from 'components/Snackbar';
import { DynamicForm } from 'components/DynamicForm';
import { ItemModalInfo } from 'components/ItemModalInfo';
import LikeLottie from '../../lottie/LikeLottie.json';

import styles from './ProductPage.module.scss';
import cx from 'classnames';

export type PageCommentsProps = {
  isAuth: boolean;
  favorites: ProductFavorite[];
  inMyFavorites: boolean;
  comments: ListResponseData<ProductComment[]>;
  setSnackNotification: Dispatch<SnackbarProps | null>;
  setModalProps: Dispatch<ModalProps | null>;
  user?: CurrentUser;
  slug: string;
  productInfo: productShortInfo;
  isOwner?: boolean;
  isAdmin?: boolean;
  toComments?: boolean;
  redirectToAuth: () => void;
  handleFavoriteButton: () => void;
  handleShareButton: (slug: string) => void;
};

export type productShortInfo = {
  image: string;
  title: string;
  owner: string;
};

const getInitialCommentFormField = (value?: string): JoinedFormFieldProps[] => [
  {
    type: 'textArea',
    fieldName: 'message',
    required: true,
    from: 1,
    to: 500,
    hint: '1-500 symbols',
    placeholder: 'Your Comment',
    mode: 'low',
    id: 'commentTextArea',
    value: value,
  },
];

const loadingCommentFormField: JoinedFormFieldProps[] = [
  {
    type: 'textArea',
    fieldName: 'none',
    from: 1,
    to: 500,
    hint: '1-500 symbols',
    placeholder: 'Sending a Comment',
    mode: 'low',
    id: 'comment',
  },
];

const prepareFavorites = (favorites: ProductFavorite[], isFavorite: boolean, user?: CurrentUser): ProductFavorite[] => {
  const maxFavoritesCount = 5;
  const userInFavorites = favorites.find((fav) => fav.username === user?.username);
  if (userInFavorites) {
    const newFavorites = favorites.filter((fav) => fav.username !== user?.username);
    isFavorite && newFavorites.push(userInFavorites);
    return newFavorites.slice(-maxFavoritesCount);
  }
  if (isFavorite && !userInFavorites) {
    const { first_name, last_name } = getUserFirstAndLastName(user!);

    favorites.push({
      first_name,
      last_name,
      username: user?.username || '',
      photo: user?.photo || '',
    });
  }
  return favorites.slice(-maxFavoritesCount);
};

export const PageComments = ({
  isAuth,
  favorites,
  inMyFavorites,
  comments,
  setSnackNotification,
  user,
  slug,
  setModalProps,
  productInfo,
  isOwner,
  isAdmin,
  toComments,
  redirectToAuth,
  handleFavoriteButton,
  handleShareButton,
}: PageCommentsProps) => {
  const upperDividerRef = useRef<HTMLDivElement>(null);
  const [shownFavorites, setShownFavorites] = useState(prepareFavorites(favorites, inMyFavorites, user));
  const [commentFormFields, setCommentFormFields] = useState<JoinedFormFieldProps[]>(getInitialCommentFormField());
  const [currentComments, setCurrentComments] = useState(comments);
  const [commentsPage, setCommentsPage] = useState(1);
  const [reasons, setReasons] = useState<SimpleItem[]>([]);
  const [refresh, setRefresh] = useState<boolean>(true);
  const formContainerRef = useRef<HTMLDivElement>(null);
  const formBottomMarkerRef = useRef<HTMLDivElement>(null);
  const formUpperMarkerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    getReasons().then();
    toComments && handleCommentButton();
    setCommentFormFields(getInitialCommentFormField())
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    setShownFavorites(prepareFavorites(favorites, inMyFavorites, user));
  }, [inMyFavorites]); // eslint-disable-line react-hooks/exhaustive-deps

  const getReasons = async () => {
    const response = await getReportReasons('item');
    if (response.ok) {
      setReasons(response.data as SimpleItem[]);
    }
  };

  const handleCommentButton = () => {
    const commentArea = formContainerRef.current?.getElementsByTagName('textarea')[0];
    commentArea?.focus();
  };

  const onReportSend = async (data: any) => {
    setModalProps(null);
    const response = await postGearData(slug, 'report', data);
    if (response.ok) {
      setSnackNotification({
        header: 'Report sent successfully',
      });
    } else {
      setSnackNotification({
        mode: 'error',
        header: response.message || 'Sorry, server responds with error. Please, try later',
      });
    }
  };

  const handleReportButton = () => {
    setModalProps({
      title: 'Report Item',
      withCrossIcon: true,
      onCoverClick: () => setModalProps(null),
      child: (() => (
        <>
          <ItemModalInfo
            title={productInfo.title}
            image={productInfo.image}
            owner={productInfo.owner}
            ownerTitle="Posted by:"
          />
          <DynamicForm
            fields={getReportFieldProps(reasons)}
            onSend={onReportSend}
            onCancel={() => setModalProps(null)}
            buttonsShape="slim"
            buttonCaption="Send Report"
            marginMode="low"
          />
        </>
      ))(),
    });
  };

  const handleReply = (commentator: string) => {
    const commentArea = formContainerRef.current?.getElementsByTagName('textarea')[0];
    if (!commentArea) return;
    const commentText = commentArea.innerHTML;
    const replyPattern = `@${commentator}`;
    if (!commentText.startsWith(replyPattern)) {
      setCommentFormFields(getInitialCommentFormField(`${replyPattern} ${commentText}`));
    }
    commentArea.focus();
    formBottomMarkerRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'nearest' });
    // formUpperMarkerRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const onCommentAdd = async (data: Record<string, string>) => {
    setCommentFormFields(loadingCommentFormField);
    const response = await postGearData(slug, 'comments', data);
    if (response.ok) {
      const newComments = await getGearComments(slug);
      setCurrentComments(newComments);
    } else {
      setSnackNotification({
        mode: 'error',
        header: response.message || 'Sorry, server responds with error. Please, try later',
      });
    }
    setCommentFormFields(getInitialCommentFormField());
    setRefresh(!refresh);
  };

  const renderButtons = () => (
    <>
      <div className={cx(styles.commentButtons, styles.notForMobile)}>
        {!isOwner && !isAdmin && (
          <ButtonLottie
            caption='Like'
            action={handleFavoriteButton}
            buttonType='white'
            shape='unset'
            fit={false}
            isActive={inMyFavorites}
            lottie={LikeLottie}
          />
        )}
        {!isAdmin && (
          <Button
            caption='Comment'
            action={isAuth ? handleCommentButton : redirectToAuth}
            buttonType='white'
            iconSrc='/icons/communications/ChatCircleDark.svg'
            shape='unset'
            fit={false}
          />
        )}
        <Button
          caption='Share'
          action={() => handleShareButton(slug)}
          buttonType='white'
          iconSrc='/icons/communications/ShareNetworkDark.svg'
          shape='unset'
          fit={false}
        />
        {!isAdmin && !isOwner && (
          <div className={styles.reportButton}>
            <Button
              caption='Report'
              action={isAuth ? handleReportButton : redirectToAuth}
              buttonType='white'
              iconSrc='/icons/security/FlagDark.svg'
              shape='unset'
              fit={false}
            />
          </div>
        )}
      </div>
      <div className={styles.forMobileOnly}>
        <div className={styles.commentButtons}>
          {!isOwner && !isAdmin && (
            <ButtonLottie
              // caption='Favorite'
              action={handleFavoriteButton}
              buttonType='white'
              shape='mobRow4'
              fit={false}
              isActive={inMyFavorites}
              lottie={LikeLottie}
            />
          )}
          {!isAdmin && (
            <Button
              // caption='Comment'
              action={isAuth ? handleCommentButton : redirectToAuth}
              buttonType='white'
              iconSrc='/icons/communications/ChatCircleDark.svg'
              shape='mobRow4'
              fit={false}
            />
          )}
          <Button
            // caption='Share'
            action={() => handleShareButton(slug)}
            buttonType='white'
            iconSrc='/icons/communications/ShareNetworkDark.svg'
            shape='mobRow4'
            fit={false}
          />
          {!isAdmin && !isOwner && (
            <Button
              // caption='Report'
              action={isAuth ? handleReportButton : redirectToAuth}
              buttonType='white'
              iconSrc='/icons/security/FlagDark.svg'
              shape='mobRow4'
              fit={false}
            />
          )}
        </div>
      </div>
    </>
  );

  const renderFavorites = () => {
    const lastFavorite = shownFavorites[shownFavorites.length - 1];

    return (
      <div className={styles.commentTopLine}>
        <div className={styles.commentFavorites}>
          {shownFavorites.map((favorite, index) => (
            <div key={index} className={styles.favoriteIcon}>
              {favorite.photo ? (
                <Image
                  src={favorite.photo ? favorite.photo : '/'}
                  layout='fill'
                  objectFit='cover'
                  alt={`${favorite.first_name[0]} ${favorite.last_name[0]}`}
                />
              ) : (
                <h4>{`${favorite.first_name[0]}${favorite.last_name[0]}`}</h4>
              )}
            </div>
          ))}
          <div className={styles.commentFavoriteCaption}>
            <span>{inMyFavorites ? 'You' : `${lastFavorite.business_name || lastFavorite.username} `}</span>
            {shownFavorites.length > 1 && (
              <>
                <p>&nbsp;and&nbsp;</p>
                <span>{` ${favorites.length - 1} others `}</span>
              </>
            )}
            <p>&nbsp;like{(favorites.length > 1 || inMyFavorites) ? '' : 's'} this.</p>
          </div>
        </div>
        {!!comments.meta?.total && (
          <div className={styles.commentComments}>
            <p>{`${comments.meta.total} comments`}</p>
          </div>
        )}
      </div>
    );
  };

  const renderComment = (comment: ProductComment, index: number) => {
    return (
      <>
        <div key={index} className={styles.commentWrapper}>
          <div className={styles.commentAvatar}>
            <Image
              src={comment.sender?.photo ? comment.sender.photo : USER_PHOTO_PLACEHOLDER_LINK}
              layout="fill"
              objectFit="cover"
              alt={comment.sender?.username}
            />
          </div>
          <div className={styles.commentBody}>
            <div className={styles.commentMessage}>
              <p>
                <span>{comment.sender?.username}</span>&nbsp;{comment.message}
              </p>
            </div>
            <div className={styles.commentDetails}>
              <p>{getTimeInterval(+comment.created_at)}</p>
              &#x2022;
              <span onClick={() => !isAuth ? redirectToAuth() : handleReply(comment.sender?.username)}>Reply</span>
            </div>
          </div>
        </div>
        <div className={styles.commentDivider} />
      </>
    );
  };

  const loadMoreComments = async () => {
    const response = await getGearComments(slug, commentsPage + 1);
    if (response.ok) {
      setCommentsPage((page) => page + 1);
      setCurrentComments((previousComments) => {
        const joinedComments = [...(previousComments.data || []), ...(response.data || [])];

        return {
          ...response,
          data: joinedComments,
        };
      });
    } else {
      setSnackNotification({
        mode: 'error',
        header: response.message || 'Sorry, server responds with error. Please, try later',
      });
    }
  };

  const loadLessComments = async () => {
    const response = await getGearComments(slug, 1);
    if (response.ok) {
      setCommentsPage(1);
      setCurrentComments(response);
    } else {
      setSnackNotification({
        mode: 'error',
        header: response.message || 'Sorry, server responds with error. Please, try later',
      });
    }
  };

  return (
    <div className={styles.comments}>
      <div className={styles.forDesktopOnly} />
      <div>
        <div ref={upperDividerRef} />
        {renderButtons()}
        {!!shownFavorites.length && renderFavorites()}
        {(!!currentComments.data?.length || isAuth) && <div className={styles.commentDivider} />}
        <>
          {currentComments.meta?.last_page > commentsPage && (
            <>
              <Button
                action={loadMoreComments}
                caption="Show Earlier Comments"
                className={styles.showCommentsButton}
                buttonHeight="low"
                buttonType="white"
                fit={false}
              />
            </>
          )}
          {commentsPage > 1 && currentComments.meta?.last_page === commentsPage && (
            <>
              <Button
                action={loadLessComments}
                caption="Hide Older Comments"
                buttonHeight="low"
                buttonType="white"
                className={styles.showCommentsButton}
                fit={false}
              />
            </>
          )}

          {!!currentComments.data?.length && [...currentComments.data].reverse().map(renderComment)}
          {isAuth && !isAdmin && (
            <div>
              <div ref={formUpperMarkerRef} />
                  <div ref={formBottomMarkerRef} />
              <div className={styles.commentUserEditor}>
                <div className={styles.commentUserAvatar}>
                  <Image
                    src={user?.photo ? user.photo : USER_PHOTO_PLACEHOLDER_LINK}
                    layout="fill"
                    objectFit="cover"
                    alt="Avatar"
                  />
                </div>
                <div ref={formContainerRef} className={styles.commentCommentForm}>
                  <DynamicForm
                    fields={commentFormFields}
                    sendButtonType="whiteBlack"
                    onSend={onCommentAdd}
                    buttonsShape="slimy"
                    buttonCaption="Post comment"
                    marginMode="none"
                    refresh={refresh}
                  />
                </div>
              </div>
            </div>
          )}
        </>
      </div>
    </div>
  );
};
